import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ErrorBoundary } from '@liveauctioneers/hammer-ui-core/providers';
import { getAuctioneerLogoUrl } from '@/utils/urls';
import {
    getCatalogAbsenteeBidders,
    getCatalogCurrency,
    getCatalogSellerId,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCurrencyConversion } from '@/redux/modules/currency';
import { getLiveCatalogBidders } from '@/redux/modules/console';
import { getMessages } from '@/redux/modules/consoleMessages';
import { getSellerLogoId } from '@/redux/modules/seller';
import { getStreamStatus, hasVideoStream } from '@/redux/modules/video';
import { getUserFullName } from '@/redux/modules/account/user/user.selectors';
import { isEqual } from 'es-toolkit';
import { MessageScroller, MessageScrollerProvider } from '@liveauctioneers/hammer-ui-liveauctioneers/messageScroller';
import { preferredCurrencySelector } from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import { useAppSelector } from '@/redux/hooks';
import { WebRTCPlayer } from '@liveauctioneers/hammer-ui-core/webRTCPlayer';

type Props = {
    catalogId: number;
};

export const BiddingConsoleRightSection = ({ catalogId }: Props) => {
    const { streamId } = useAppSelector((state) => getStreamStatus(state, catalogId));
    const smallConsole = false;
    const messageList = useAppSelector((state) => getMessages(state, catalogId, smallConsole), isEqual) || [];
    const sellerId = useAppSelector((state) => getCatalogSellerId(state, catalogId));
    const logoId = useAppSelector((state) => getSellerLogoId(state, sellerId));
    const userName = useAppSelector((state) => getUserFullName(state));

    // Currency conversion data
    const sourceCurrency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const preferredCurrency = useAppSelector(preferredCurrencySelector);
    const preferredCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, preferredCurrency));
    const sourceCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, sourceCurrency));

    // Viewers count
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId));
    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId));
    const catalogHasVideoStream = useAppSelector((state) => hasVideoStream(state, catalogId));

    return (
        <div
            className={cn('grid h-full grid-rows-1 flex-col gap-md', {
                'grid-rows-[max-content_1fr]': catalogHasVideoStream,
            })}
        >
            {catalogHasVideoStream && (
                <WebRTCPlayer
                    className="aspect-auto min-h-[226px] rounded-sm"
                    debug={false}
                    streamId={streamId}
                />
            )}
            <div className="h-full rounded-sm bg-background-primary">
                <ErrorBoundary meta={{ name: 'MessageScroller' }}>
                    <MessageScrollerProvider
                        data={{
                            auctioneerInfo: {
                                logoUrl: getAuctioneerLogoUrl(logoId),
                            },
                            convert: true,
                            messages: messageList,
                            preferredCurrencyConversion,
                            sourceCurrencyConversion,
                            userName,
                            viewerCount: bidders + absenteeBidders || 1,
                        }}
                    >
                        <MessageScroller />
                    </MessageScrollerProvider>
                </ErrorBoundary>
            </div>
        </div>
    );
};
